import { useTranslation } from '../../../i18n';
import React from 'react';
import { Table, Empty, theme, Typography } from 'antd';

const { Text } = Typography;

/**
 * Table on desktop, compact stacked list on mobile.
 * Columns follow antd's shape; a column with `key: 'actions'` is rendered as the row footer on mobile.
 */
const CompactTable = ({ isMobile, columns, dataSource, rowKey = 'id', loading, emptyText = 'No data yet', expandable }) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const getKey = (r, i) => (typeof rowKey === 'function' ? rowKey(r) : r[rowKey] ?? i);

    if (!isMobile) {
        return (
            <Table
                size="small"
                rowKey={rowKey}
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={false}
                expandable={expandable}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={typeof emptyText === 'string' ? t(emptyText) : emptyText} /> }}
            />
        );
    }

    if (!loading && (!dataSource || dataSource.length === 0)) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={typeof emptyText === 'string' ? t(emptyText) : emptyText} />;
    }

    const cellOf = (col, record, index) => {
        const value = Array.isArray(col.dataIndex)
            ? col.dataIndex.reduce((o, k) => o?.[k], record)
            : col.dataIndex !== undefined ? record[col.dataIndex] : undefined;
        return col.render ? col.render(value, record, index) : (value ?? '-');
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: loading ? 0.5 : 1 }}>
            {(dataSource || []).map((record, index) => {
                const actionCol = columns.find((c) => c.key === 'actions');
                const fields = columns.filter((c) => c.key !== 'actions');
                return (
                    <div
                        key={getKey(record, index)}
                        style={{
                            border: `1px solid ${token.colorBorderSecondary}`,
                            borderRadius: token.borderRadius,
                            padding: '8px 12px',
                            background: token.colorBgContainer,
                        }}
                    >
                        {fields.map((col, ci) => (
                            <div
                                key={col.key || col.dataIndex || ci}
                                style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '2px 0', fontSize: 13 }}
                            >
                                <Text type="secondary" style={{ flexShrink: 0 }}>{col.title}</Text>
                                <span style={{ textAlign: 'right', minWidth: 0, wordBreak: 'break-all' }}>
                                    {cellOf(col, record, index)}
                                </span>
                            </div>
                        ))}
                        {expandable?.expandedRowRender && expandable.rowExpandable?.(record) !== false && (
                            <div style={{ marginTop: 6 }}>{expandable.expandedRowRender(record)}</div>
                        )}
                        {actionCol && (
                            <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
                                {cellOf(actionCol, record, index)}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default CompactTable;
